/*global FB*/
/*eslint no-undef: "error"*/

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Modal from '../Modal';

import HingeFadeInFromTop from '../../transitions/HingeFadeInFromTop';

import copyToClipboard from '../../utils/copyToClipboard';
import { useTranslation } from 'next-i18next';

import styles from './ShareModal.module.scss';

import Icon from '../Icon';

const ShareModal = ({ heading, shareTitle, href, visible, toggleVisible }) => {
    const { t } = useTranslation('common');
    const [modalContentVisible, setModalContentVisible] = useState(false);
    const [showCopiedLabel, setShowCopiedLabel] = useState(false);

    // uncomment the effect below to enable fb sdk functionality
    // when developing with a dev server
    // NOTE: you may have to create a new test fb app id

    // useEffect(() => {
    //     window.fbAsyncInit = function () {
    //         FB.init({
    //             appId: '939213059773732',
    //             autoLogAppEvents: true,
    //             xfbml: true,
    //             version: 'v4.0'
    //         });
    //     };

    //     if (typeof FB === 'undefined') {
    //         const script = document.createElement('script');
    //         script.src = 'https://connect.facebook.net/en_US/sdk.js';
    //         script.async = true;
    //         document.body.appendChild(script);
    //     }
    // });

    // trigger hinge in animation of modal content
    useEffect(() => {
        setModalContentVisible(visible);
    }, [visible]);

    const notSsr = typeof window !== 'undefined';
    const canonicalUrlEl = notSsr ? document.querySelector('link[rel=canonical]') : '';

    const modalHeading = heading ? heading : t('sharemodal.share');

    const title = shareTitle ? shareTitle : notSsr ? document.title : '';

    const shareUrl = href ? href : canonicalUrlEl ? canonicalUrlEl.href : notSsr ? document.location.href : '';

    const createEmailShareUrl = () => {
        const subject = encodeURIComponent(`${t('sharemodal.shareemailsubject')} - ${title}`);
        const body = encodeURIComponent(`${t('sharemodal.shareemailbody')}:\n${title} - ${shareUrl}`);

        return `mailto: email@email.mail?subject=${subject}&body=${body}`;
    };

    const triggerFbSdfkShare = (shareType = 'messenger') => {
        if (typeof FB === 'undefined') {
            console.error('Error: the Facebook SDK is not available!');
            return;
        }

        FB.ui({
            method: shareType === 'messenger' ? 'send' : 'share',
            link: shareUrl,
            href: shareUrl,
        });
    };

    const triggerFacebookShare = () => triggerFbSdfkShare('regular');
    const triggerMessengerShare = () => triggerFbSdfkShare('messenger');

    const toggleShareDialog = () => {
        toggleVisible();
        setTimeout(() => setModalContentVisible(!modalContentVisible), 100);
    };

    const handleCopyToClipboard = () => {
        if (showCopiedLabel) {
            return;
        }

        copyToClipboard(shareUrl);
        setShowCopiedLabel(true);
        setTimeout(() => setShowCopiedLabel(false), 2000);
    };

    return (
        <div className={styles['ShareModal']}>
            <Modal open={visible} closeLabel={t('sharemodal.close')} handleClose={toggleShareDialog}>
                <div>
                    <HingeFadeInFromTop visible={modalContentVisible}>
                        <div className={styles['ShareModal__Content']}>
                            <h2 className={styles['ShareModal__Title']}>{modalHeading}</h2>
                            <div className={styles['ShareModal__SocialButtons']}>
                                <button
                                    className={
                                        styles['ShareModal__SocialButton'] +
                                        ' ' +
                                        styles['ShareModal__SocialButton--Messenger']
                                    }
                                    onClick={triggerMessengerShare}>
                                    {
                                        <Icon
                                            className={
                                                styles['ShareModal__SocialButton__Icon'] +
                                                ' ' +
                                                styles['ShareModal__SocialButton__Icon--Messenger']
                                            }
                                            name={'SocialTmpMessenger'}
                                        />
                                    }
                                    <span className="sr-only">{t('sharemodal.sharemessenger')}</span>
                                </button>
                                <button
                                    className={
                                        styles['ShareModal__SocialButton'] +
                                        ' ' +
                                        styles['ShareModal__SocialButton--Fb']
                                    }
                                    onClick={triggerFacebookShare}>
                                    {
                                        <Icon
                                            className={
                                                styles['ShareModal__SocialButton__Icon'] +
                                                ' ' +
                                                styles['ShareModal__SocialButton__Icon--Fb']
                                            }
                                            name={'SocialFB'}
                                        />
                                    }
                                    <span className="sr-only">{t('sharemodal.sharefacebook')}</span>
                                </button>
                                <a
                                    className={
                                        styles['ShareModal__SocialButton'] +
                                        ' ' +
                                        styles['ShareModal__SocialButton--Email']
                                    }
                                    href={createEmailShareUrl()}>
                                    {
                                        <Icon
                                            className={
                                                styles['ShareModal__SocialButton__Icon'] +
                                                ' ' +
                                                styles['ShareModal__SocialButton__Icon--Mail']
                                            }
                                            name={'IconShareMail'}
                                        />
                                    }
                                    <span className="sr-only">{t('sharemodal.sharefacebook')}</span>
                                </a>
                            </div>

                            <div className={styles['ShareModal__UrlInput']}>
                                <input className={styles['ShareModal__Input']} type="text" readOnly value={shareUrl} />
                                <button className={styles['ShareModal__CopyButton']} onClick={handleCopyToClipboard}>
                                    <div className={styles['ShareModal__CopyButton__Labels']}>
                                        <span className={styles['ShareModal__CopyButton__Label']}>
                                            {showCopiedLabel && t('sharemodal.copied')}
                                            {!showCopiedLabel && t('sharemodal.copylink')}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </HingeFadeInFromTop>
                </div>
            </Modal>
        </div>
    );
};

ShareModal.propTypes = {
    heading: PropTypes.string,
    preamble: PropTypes.string,
    shareTitle: PropTypes.string,
    href: PropTypes.string,
    visible: PropTypes.bool,
    toggleVisible: PropTypes.func,
};

ShareModal.defaultProps = {
    heading: null,
    preamble: null,
    shareTitle: null,
    href: null,
    visible: false,
    toggleVisible: () => {},
};

export default ShareModal;
